"use client";

import Link from "next/link";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import {
  defaultLocale,
  getLocalizedPathname,
  type Locale,
} from "@/lib/i18n";
import { agentUseCases } from "@/content/agent-use-cases";
import {
  getUseCaseContent,
  patternLabels,
} from "@/content/agent-use-cases/types";
import {
  industryPages,
  getIndustriesForUseCase,
} from "@/content/industries";
import { StructuredData } from "@/components/common";
import { whatsappHref } from "@/lib/whatsapp";

const WHATSAPP = "601159246128";

// Compact 1–5 dot meter for the card's value / build-effort scores.
function Meter({ score, max = 5 }: { score: number; max?: number }) {
  return (
    <span
      className="inline-flex items-center gap-0.5"
      role="img"
      aria-label={`${score} of ${max}`}
    >
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${
            i < score ? "bg-primary" : "bg-primary/20"
          }`}
        />
      ))}
    </span>
  );
}

const strings: Record<
  Locale,
  {
    breadcrumbHome: string;
    breadcrumbCurrent: string;
    eyebrow: string;
    title: string;
    subhead: string;
    credibility: string;
    processSource: string;
    industries: string;
    methodLabel: string;
    methodSteps: string[];
    availableCta: string;
    requestCta: string;
    availableBadge: string;
    requestBadge: string;
    browseByIndustry: string;
    valueWord: string;
    buildWord: string;
    requestPrefill: (t: string) => string;
    bottomTitle: string;
    bottomDesc: string;
    bottomCta: string;
    faqHeading: string;
    faqs: { q: string; a: string }[];
  }
> = {
  en: {
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Agent use cases",
    eyebrow: "AI agent use cases · Malaysia & Singapore",
    title: "AI agent development, shown use case by use case",
    subhead:
      "For each use case we show the manual workflow, the agentic redesign, how we build the loop on Claude, and how we prove it works — then build it for you.",
    credibility:
      "Certified in the Claude Partner Network. We use the agent patterns, not just the buzzword.",
    processSource:
      "Built on Anthropic's agent guidance and the Agent GPA eval framework.",
    industries:
      "Use cases across finance, insurance, retail & F&B, procurement & manufacturing, legal, HR and customer service.",
    methodLabel: "Our agentic development process",
    methodSteps: [
      "Frame the problem",
      "Map the manual workflow",
      "Design the agentic workflow",
      "Build the loop on Claude",
      "Single model or multi-agent",
      "Evaluate & measure",
      "Deliver, POC-first",
    ],
    availableCta: "See how we'd build it",
    requestCta: "Request this use case",
    availableBadge: "Available",
    requestBadge: "Request",
    browseByIndustry: "Browse by industry:",
    valueWord: "Value",
    buildWord: "Build effort",
    requestPrefill: (t) =>
      `Hi Anchor Sprint, I'd like to explore an AI agent for: ${t}`,
    bottomTitle: "Don't see your use case?",
    bottomDesc:
      "If your team spends its day reading, checking and matching documents or requests by hand, there's probably an agent for it. Tell us the problem.",
    bottomCta: "Tell us the problem",
    faqHeading: "AI agent development — common questions",
    faqs: [
      {
        q: "How do you build an AI agent for a business in the region?",
        a: "We frame the problem, map the manual workflow, design the agentic loop, and build it on Claude using the Claude Agent SDK and the Model Context Protocol (MCP) — POC-first, so you see a working agent before committing to the full build.",
      },
      {
        q: "How much does AI agent development cost?",
        a: "A first, well-scoped agent typically lands in the low tens of thousands of Ringgit, plus running costs for the Claude model and infrastructure. You can model the running cost for your team with our Claude cost calculator.",
      },
      {
        q: "Which industries use these AI agents?",
        a: "Finance, insurance, retail and F&B, procurement and manufacturing, legal, HR and customer service — anywhere teams read, check and match documents or requests by hand.",
      },
    ],
  },
  ms: {
    breadcrumbHome: "Laman Utama",
    breadcrumbCurrent: "Kes penggunaan ejen",
    eyebrow: "Kes penggunaan ejen AI · Malaysia & Singapura",
    title: "Pembangunan ejen AI, ditunjukkan kes demi kes",
    subhead:
      "Bagi setiap kes penggunaan kami tunjukkan aliran kerja manual, reka bentuk semula agentik, cara kami membina gelung atas Claude, dan cara kami membuktikannya berfungsi — kemudian membinanya untuk anda.",
    credibility:
      "Diperakui dalam Claude Partner Network. Kami menggunakan corak ejen, bukan sekadar kata kunci.",
    processSource:
      "Dibina atas panduan ejen Anthropic dan rangka kerja penilaian Agent GPA.",
    industries:
      "Kes penggunaan merentas kewangan, insurans, runcit & F&B, perolehan & pembuatan, perundangan, HR dan khidmat pelanggan.",
    methodLabel: "Proses pembangunan agentik kami",
    methodSteps: [
      "Rangka masalah",
      "Petakan aliran kerja manual",
      "Reka bentuk aliran kerja agentik",
      "Bina gelung atas Claude",
      "Model tunggal atau berbilang-ejen",
      "Nilai & ukur",
      "Sampaikan, POC dahulu",
    ],
    availableCta: "Lihat cara kami membinanya",
    requestCta: "Minta kes penggunaan ini",
    availableBadge: "Tersedia",
    requestBadge: "Minta",
    browseByIndustry: "Semak ikut industri:",
    valueWord: "Nilai",
    buildWord: "Usaha bina",
    requestPrefill: (t) =>
      `Hai Anchor Sprint, saya ingin meneroka ejen AI untuk: ${t}`,
    bottomTitle: "Tidak nampak kes penggunaan anda?",
    bottomDesc:
      "Jika pasukan anda menghabiskan hari membaca, menyemak dan memadan dokumen atau permintaan secara manual, kemungkinan besar ada ejen untuknya. Beritahu kami masalahnya.",
    bottomCta: "Beritahu kami masalahnya",
    faqHeading: "Pembangunan ejen AI — soalan lazim",
    faqs: [
      {
        q: "Bagaimana anda membina ejen AI untuk perniagaan di rantau ini?",
        a: "Kami merangka masalah, memetakan aliran kerja manual, mereka bentuk gelung agentik, dan membinanya atas Claude menggunakan Claude Agent SDK dan Model Context Protocol (MCP) — POC dahulu, jadi anda nampak ejen berfungsi sebelum komited pada pembinaan penuh.",
      },
      {
        q: "Berapa kos pembangunan ejen AI?",
        a: "Ejen pertama yang berskop baik biasanya sekitar puluhan ribu Ringgit rendah, tambah kos operasi untuk model Claude dan infrastruktur. Anda boleh anggarkan kos operasi pasukan anda dengan kalkulator kos Claude kami.",
      },
      {
        q: "Industri manakah yang menggunakan ejen AI ini?",
        a: "Kewangan, insurans, runcit & F&B, perolehan & pembuatan, perundangan, HR dan khidmat pelanggan — di mana sahaja pasukan membaca, menyemak dan memadan dokumen atau permintaan secara manual.",
      },
    ],
  },
  zh: {
    breadcrumbHome: "首页",
    breadcrumbCurrent: "智能体应用场景",
    eyebrow: "AI 智能体应用场景 · 马来西亚与新加坡",
    title: "AI 智能体开发，按应用场景逐一展示",
    subhead:
      "对每个应用场景，我们展示人工流程、智能体化的重新设计、如何在 Claude 上构建循环，以及如何验证其有效——然后为你构建。",
    credibility: "Claude Partner Network 认证合作伙伴。我们运用智能体模式，而不只是喊口号。",
    processSource: "建立在 Anthropic 的智能体指南与 Agent GPA 评估框架之上。",
    industries: "涵盖金融、保险、零售与餐饮、采购与制造、法务、人力资源及客户服务等行业的应用场景。",
    methodLabel: "我们的智能体开发流程",
    methodSteps: [
      "界定问题",
      "梳理人工流程",
      "设计智能体化流程",
      "在 Claude 上构建循环",
      "单一模型还是多智能体",
      "评估与衡量",
      "交付，先做 POC",
    ],
    availableCta: "看我们如何构建",
    requestCta: "申请此应用场景",
    availableBadge: "已上线",
    requestBadge: "可申请",
    browseByIndustry: "按行业浏览：",
    valueWord: "价值",
    buildWord: "构建难度",
    requestPrefill: (t) => `你好 Anchor Sprint，我想探讨用于以下场景的 AI 智能体：${t}`,
    bottomTitle: "没有找到你的应用场景？",
    bottomDesc:
      "如果你的团队整天在人工读取、核对和比对文件或请求，那很可能有一个智能体能胜任。告诉我们你的问题。",
    bottomCta: "告诉我们你的问题",
    faqHeading: "AI 智能体开发 — 常见问题",
    faqs: [
      {
        q: "如何为区域内的企业构建 AI 智能体？",
        a: "我们界定问题、梳理人工流程、设计智能体循环，并使用 Claude Agent SDK 与 Model Context Protocol (MCP) 在 Claude 上构建——先做 POC，让你在投入完整构建前先看到可运行的智能体。",
      },
      {
        q: "开发 AI 智能体的成本是多少？",
        a: "第一个范围明确的智能体通常在低位数万令吉，另加 Claude 模型与基础设施的运行成本。你可以用我们的 Claude 成本计算器估算团队的运行成本。",
      },
      {
        q: "哪些行业在使用这些 AI 智能体？",
        a: "金融、保险、零售与餐饮、采购与制造、法务、人力资源及客户服务——凡是团队需要人工读取、核对和比对文件或请求的地方。",
      },
    ],
  },
};

export default function AgentUseCasesHub({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const s = strings[locale] ?? strings[defaultLocale];

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="bg-background pt-24 pb-10 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-muted">
            <Link href="/" className="transition-colors hover:text-primary">
              {s.breadcrumbHome}
            </Link>
            <span className="mx-2" aria-hidden>
              /
            </span>
            <span className="text-neutral-emphasis">{s.breadcrumbCurrent}</span>
          </nav>
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {s.eyebrow}
          </p>
          <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl">
            {s.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-muted">
            {s.subhead}
          </p>
          <p className="mt-4 text-sm font-medium text-primary">
            {s.credibility}
          </p>
          <p className="mt-3 max-w-2xl text-sm text-neutral-muted">
            {s.industries}
          </p>

          {/* Method strip */}
          <div className="mt-10 rounded-2xl border border-border bg-card p-6">
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-muted">
              {s.methodLabel}
            </p>
            <p className="mb-4 text-xs text-neutral-muted">
              {s.processSource}{" "}
              <a
                href="https://www.anthropic.com/research/building-effective-agents"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
              >
                Building Effective Agents
              </a>
              {" · "}
              <a
                href="https://arxiv.org/abs/2510.08847"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline decoration-dotted underline-offset-2 hover:text-primary-emphasis"
              >
                Agent GPA
              </a>
            </p>
            <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {s.methodSteps.map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {i + 1}
                  </span>
                  <span className="text-sm text-neutral-emphasis">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Card grid */}
      <section className="bg-background pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-neutral-emphasis">
              {s.browseByIndustry}
            </span>
            {industryPages.map((ind) => (
              <Link
                key={ind.key}
                href={getLocalizedPathname(`/${ind.route}/`, locale)}
                className="rounded-full border border-border px-3 py-1 font-medium text-neutral-muted transition hover:border-primary hover:text-primary"
              >
                {ind.cardLabel[locale] ?? ind.cardLabel.en}
              </Link>
            ))}
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {agentUseCases.map((useCase) => {
              const c = getUseCaseContent(useCase, locale);
              const { structure } = useCase;
              const available = structure.status === "available";
              const industryPage = getIndustriesForUseCase(structure.slug)[0];

              return (
                <article
                  key={structure.slug}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  {/* Card image */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-neutral-surface">
                    {structure.cardImage && (
                      <img
                        src={structure.cardImage}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        width={1536}
                        height={1024}
                        decoding="async"
                      />
                    )}
                    {industryPage ? (
                      <Link
                        href={getLocalizedPathname(
                          `/${industryPage.route}/`,
                          locale,
                        )}
                        className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-emphasis backdrop-blur transition hover:bg-primary hover:text-primary-foreground"
                      >
                        {c.industry}
                      </Link>
                    ) : (
                      <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-emphasis backdrop-blur">
                        {c.industry}
                      </span>
                    )}
                    <span
                      className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                        available
                          ? "bg-primary text-primary-foreground"
                          : "bg-background/90 text-neutral-muted backdrop-blur"
                      }`}
                    >
                      {available ? s.availableBadge : s.requestBadge}
                    </span>
                  </div>

                  {/* Card body */}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="mb-2 w-fit rounded-full bg-primary/10 px-2.5 py-0.5 font-mono text-[10px] font-medium text-primary">
                      {patternLabels[structure.pattern]}
                    </span>
                    <h2 className="font-display text-xl font-semibold text-neutral-emphasis">
                      {c.title}
                    </h2>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-muted">
                      {c.cardSummary}
                    </p>

                    {available &&
                      structure.valueScore &&
                      structure.complexityScore && (
                        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-border pt-4 text-xs">
                          <span className="flex items-center gap-1.5">
                            <span className="text-neutral-muted">
                              {s.valueWord}
                            </span>
                            <Meter score={structure.valueScore} />
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span className="text-neutral-muted">
                              {s.buildWord}
                            </span>
                            <Meter score={structure.complexityScore} />
                          </span>
                        </div>
                      )}

                    {available && structure.route ? (
                      <Link
                        href={getLocalizedPathname(`/${structure.route}/`, locale)}
                        data-track-event="use_case_view"
                        data-track-location={structure.trackLocation}
                        className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5"
                      >
                        {s.availableCta}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    ) : (
                      <a
                        href={whatsappHref(
                          s.requestPrefill(c.title),
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-track-event="contact_whatsapp_click"
                        data-track-location={structure.trackLocation}
                        data-track-phone={WHATSAPP}
                        className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-emphasis transition-colors hover:text-primary"
                      >
                        <FaWhatsapp className="h-4 w-4" aria-hidden />
                        {s.requestCta}
                        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-border bg-card p-8 sm:flex-row sm:items-center">
            <div>
              <h2 className="font-display text-2xl font-semibold text-neutral-emphasis">
                {s.bottomTitle}
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-muted">
                {s.bottomDesc}
              </p>
            </div>
            <a
              href={whatsappHref(
                s.requestPrefill("a process we do by hand"),
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-track-event="contact_whatsapp_click"
              data-track-location="agent_use_cases_hub_footer"
              data-track-phone={WHATSAPP}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              <FaWhatsapp className="h-4 w-4" aria-hidden />
              {s.bottomCta}
            </a>
          </div>

          {/* Hub-level FAQ — visible + FAQPage schema (AEO); single source */}
          <StructuredData
            type="faq"
            faqs={s.faqs.map((f) => ({ question: f.q, answer: f.a }))}
          />
          <div className="mt-12">
            <h2 className="mb-6 font-display text-2xl font-semibold text-neutral-emphasis">
              {s.faqHeading}
            </h2>
            <dl className="space-y-5">
              {s.faqs.map((f) => (
                <div key={f.q}>
                  <dt className="font-semibold text-neutral-emphasis">{f.q}</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-neutral-muted">
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>
    </div>
  );
}
